import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { apiClient } from '../lib/api';
import {
  ReceiveShareAuthResponse,
  OTPSendResponse,
  OTPVerifyResponse,
  SharePublicDetail
} from '@df-gate-share/shared';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import MkdfLogo from '../components/MkdfLogo';
import DFFilesLogo from '../components/DFFilesLogo';
import MessageDialog from '../components/MessageDialog';
import { useMessageDialog } from '../hooks/useMessageDialog';

type OTPPhase = 'loading' | 'auth' | 'otp-sent' | 'verified' | 'error' | 'expired' | 'invalid';

interface OTPError {
  message: string;
  remainingAttempts?: number;
  requiresResend?: boolean;
}

const OTPVerification: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const { dialogState, closeDialog, showAlert } = useMessageDialog();

  // Auth state
  const [phase, setPhase] = useState<OTPPhase>('loading');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [shareTitle, setShareTitle] = useState('');

  // OTP state
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState<OTPError | null>(null);
  const [otpSuccess, setOtpSuccess] = useState('');

  // Rate limit state
  const [retryAfter, setRetryAfter] = useState(0);

  // Session state
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [shareDetail, setShareDetail] = useState<SharePublicDetail | null>(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);

  // Download state
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<{ current: number; total: number } | null>(null);

  // Error state
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch share info on mount
  useEffect(() => {
    fetchShareInfo();
  }, [shareToken]);

  // Countdown timer for rate limiting
  useEffect(() => {
    if (retryAfter <= 0) return;
    const timer = setInterval(() => {
      setRetryAfter(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [retryAfter]);

  // Session token expiry monitoring
  useEffect(() => {
    if (!sessionExpiresAt) return;
    const timer = setInterval(() => {
      if (Date.now() >= sessionExpiresAt) {
        setPhase('auth');
        setSessionToken(null);
        setShareDetail(null);
        setSessionExpiresAt(null);
        setOtpCode('');
        setOtpSuccess('');
        setOtpError(null);
      }
    }, 10000);
    return () => clearInterval(timer);
  }, [sessionExpiresAt]);

  const fetchShareInfo = async () => {
    try {
      const response = await apiClient.get(`/api/v1/receive/${shareToken}`);
      const data = response.data;

      if (data.requiresAuth) {
        const authData = data as ReceiveShareAuthResponse;
        setMaskedEmail(authData.maskedEmail);
        setShareTitle(authData.shareTitle);
        setPhase('auth');
      }
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 404) {
        setErrorMessage('共有リンクが見つかりません。');
        setPhase('error');
      } else if (status === 410) {
        setPhase('expired');
      } else {
        setErrorMessage('エラーが発生しました。');
        setPhase('error');
      }
    }
  };

  const handleSendOTP = async () => {
    if (otpSending || retryAfter > 0) return;

    setOtpSending(true);
    setOtpError(null);
    setOtpSuccess('');

    try {
      const response = await apiClient.post<OTPSendResponse>(
        `/api/v1/receive/${shareToken}/send-otp`
      );
      setOtpSuccess('認証コードをメールに送信しました');
      setRetryAfter(response.data.retryAfter);
      setPhase('otp-sent');
    } catch (err) {
      const axiosErr = axios.isAxiosError(err) ? err : undefined;
      const status = axiosErr?.response?.status;
      if (status === 429) {
        const retryAfterHeader = axiosErr?.response?.data?.retryAfter || 60;
        setRetryAfter(retryAfterHeader);
        setOtpError({ message: '送信回数の上限に達しました。しばらくお待ちください。' });
      } else if (status === 410) {
        setPhase('expired');
      } else {
        setOtpError({ message: '認証コードの送信に失敗しました。' });
      }
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otpVerifying || otpCode.length !== 6) return;

    setOtpVerifying(true);
    setOtpError(null);

    try {
      const response = await apiClient.post<OTPVerifyResponse>(
        `/api/v1/receive/${shareToken}/verify-otp`,
        { code: otpCode }
      );
      setSessionToken(response.data.sessionToken);
      setShareDetail(response.data.share);
      // Set session expiry (30 minutes from now)
      setSessionExpiresAt(Date.now() + 30 * 60 * 1000);
      setPhase('verified');
    } catch (err) {
      const axiosErr = axios.isAxiosError(err) ? err : undefined;
      const status = axiosErr?.response?.status;
      if (status === 401) {
        const remainingAttempts = axiosErr?.response?.data?.remainingAttempts;
        if (remainingAttempts === 0) {
          setOtpError({
            message: '試行回数の上限に達しました。認証コードを再送信してください。',
            remainingAttempts: 0,
            requiresResend: true
          });
          setOtpCode('');
        } else {
          setOtpError({
            message: '認証コードが正しくありません',
            remainingAttempts
          });
        }
      } else if (status === 410) {
        if (axiosErr?.response?.data?.error?.code === 'OTP_EXPIRED') {
          setOtpError({
            message: '認証コードの有効期限が切れました。再送信してください。',
            requiresResend: true
          });
        } else {
          setPhase('expired');
        }
      } else if (status === 429) {
        setOtpError({ message: '試行回数が多すぎます。しばらくお待ちください。' });
      } else {
        setOtpError({ message: '認証に失敗しました。' });
      }
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleOtpCodeChange = (value: string) => {
    // Only allow digits, max 6 characters
    const digitsOnly = value.replace(/\D/g, '').slice(0, 6);
    setOtpCode(digitsOnly);
  };

  const handleDownload = async (fileId: string, fileName: string) => {
    try {
      const response = await apiClient.get(
        `/api/v1/receive/${shareToken}/download/${fileId}`,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      window.open(response.data.downloadUrl, '_blank');
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 401) {
        setPhase('auth');
        setSessionToken(null);
        setShareDetail(null);
      } else {
        showAlert('error', 'エラー', 'ダウンロードに失敗しました。');
      }
    }
  };

  const handleDownloadAll = async () => {
    if (!shareDetail) return;

    try {
      setDownloading(true);
      setDownloadProgress(null);

      const response = await apiClient.get(
        `/api/v1/receive/${shareToken}/download-all`,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );

      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      } else if (response.data.files && Array.isArray(response.data.files)) {
        const zip = new JSZip();
        const files = response.data.files;
        setDownloadProgress({ current: 0, total: files.length });

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          try {
            setDownloadProgress({ current: i + 1, total: files.length });
            const fileResponse = await fetch(file.downloadUrl);
            if (!fileResponse.ok) throw new Error(`Failed to download ${file.fileName}`);
            const blob = await fileResponse.blob();
            zip.file(file.fileName, blob);
          } catch (fileError) {
            console.error(`Error downloading ${file.fileName}:`, fileError);
          }
        }

        setDownloadProgress(null);
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const zipFileName = shareDetail.title
          ? `${shareDetail.title.replace(/[^a-zA-Z0-9_\-\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/g, '_')}.zip`
          : 'download.zip';
        saveAs(zipBlob, zipFileName);
      }
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      if (status === 401) {
        setPhase('auth');
        setSessionToken(null);
        setShareDetail(null);
      } else {
        showAlert('error', 'エラー', 'ダウンロードに失敗しました。');
      }
    } finally {
      setDownloading(false);
      setDownloadProgress(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getDaysRemaining = (expiresAt: string): string => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diff = expiry.getTime() - now.getTime();
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    if (days < 0) return '期限切れ';
    if (days === 0) return '今日まで';
    return `あと${days}日`;
  };

  const getFileIcon = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'description';
    if (['xlsx', 'xls', 'csv'].includes(ext || '')) return 'bar_chart';
    if (['docx', 'doc'].includes(ext || '')) return 'edit_note';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(ext || '')) return 'image';
    return 'attach_file';
  };

  // Loading state
  if (phase === 'loading') {
    return (
      <div className="hero-gradient">
        <div className="hero-card text-center">
          <div className="hero-card__body">
            <div className="text-muted">読み込み中...</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (phase === 'error') {
    return (
      <div className="hero-gradient">
        <div className="hero-card">
          <div className="hero-card__body text-center">
            <div className="empty-state__icon"><span className="material-symbols-outlined icon-xl">error</span></div>
            <h2 className="empty-state__title mb-3">エラー</h2>
            <p className="text-muted text-sm">{errorMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  // Expired state
  if (phase === 'expired') {
    return (
      <div className="hero-gradient">
        <div className="hero-card">
          <div className="hero-card__body text-center">
            <div className="empty-state__icon"><span className="material-symbols-outlined icon-xl">timer_off</span></div>
            <h2 className="empty-state__title mb-3">有効期限が切れています</h2>
            <p className="text-muted text-sm">
              このファイルの共有期限は終了しました。<br />
              ファイルにアクセスする必要がある場合は、送信者にお問い合わせください。
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Verified state - show share detail and download UI
  if (phase === 'verified' && shareDetail) {
    return (
      <div className="hero-gradient">
        <div className="hero-card hero-card--wide">
          {/* Header */}
          <div className="hero-card__header">
            <div className="hero-card__logo">
              <MkdfLogo height={28} />
              <span className="header__brand-divider" style={{ margin: '0 8px', opacity: 0.3 }}>|</span>
              <DFFilesLogo size={24} variant="light" />
            </div>
            <div className="hero-card__tagline">
              セキュアなファイル共有ソリューション
            </div>
          </div>

          {/* Content */}
          <div className="hero-card__body">
            {/* Sender Info */}
            <div className="card card__body mb-8 flex items-center gap-4" style={{ borderLeft: '4px solid var(--color-cyan-500)' }}>
              <div className="avatar avatar--lg">
                <span className="material-symbols-outlined">person</span>
              </div>
              <div>
                <h3 className="font-semibold mb-1">
                  {shareDetail.senderName || shareDetail.senderEmail} さんからファイルが届いています
                </h3>
                <div className="text-muted text-sm">
                  {shareDetail.senderEmail}
                </div>
              </div>
            </div>

            {/* Title Section */}
            <div className="mb-8">
              <h1 className="page-title mb-3">
                {shareDetail.title}
              </h1>
              <div className="badge badge--warning">
                <span className="material-symbols-outlined icon-sm">schedule</span> 有効期限: {getDaysRemaining(shareDetail.expiresAt)}（{new Date(shareDetail.expiresAt).toLocaleString('ja-JP')}まで）
              </div>
            </div>

            {/* Message Section */}
            {shareDetail.message && (
              <div className="mb-8">
                <div className="font-semibold text-sm mb-2">
                  <span className="material-symbols-outlined icon-sm">edit_note</span> 送信者からのメッセージ
                </div>
                <div className="card card__body" style={{ whiteSpace: 'pre-wrap' }}>
                  {shareDetail.message}
                </div>
              </div>
            )}

            {/* Files Section */}
            <div className="mb-8">
              <h2 className="font-semibold mb-4">
                <span className="material-symbols-outlined icon-sm">attach_file</span> 添付ファイル（{shareDetail.files.length}ファイル、合計 {formatFileSize(shareDetail.totalSize)}）
              </h2>
              <div className="flex flex-col gap-3">
                {shareDetail.files.map((file) => (
                  <div key={file.fileId} className="file-item">
                    <div className="file-item__info">
                      <span className="file-item__icon"><span className="material-symbols-outlined">{getFileIcon(file.fileName)}</span></span>
                      <div>
                        <div className="file-item__name">{file.fileName}</div>
                        <div className="file-item__size">{formatFileSize(file.fileSize)}</div>
                      </div>
                    </div>
                    <button
                      className="btn btn--primary"
                      onClick={() => handleDownload(file.fileId, file.fileName)}
                    >
                      <span className="material-symbols-outlined icon-sm">download</span> ダウンロード
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Download All Button */}
            {shareDetail.files.length > 1 && (
              <div className="mb-8">
                <button
                  className="btn btn--primary btn--block btn--lg"
                  onClick={handleDownloadAll}
                  disabled={downloading}
                >
                  <span className="material-symbols-outlined icon-sm">{downloading ? 'hourglass_top' : 'download'}</span>
                  {downloading && downloadProgress
                    ? `ダウンロード中... (${downloadProgress.current}/${downloadProgress.total})`
                    : downloading
                    ? 'ZIP生成中...'
                    : 'すべてのファイルをダウンロード（ZIP形式）'}
                </button>
              </div>
            )}

            {/* Info Section */}
            <div className="info-box info-box--info">
              <div className="font-semibold text-sm mb-3 flex items-center gap-2">
                <span className="material-symbols-outlined icon-sm">info</span>
                <span>ご利用上の注意</span>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  'このファイルは送信者によって暗号化された状態で保存されています',
                  '有効期限を過ぎるとダウンロードできなくなります',
                  'ダウンロードした記録は送信者に通知されます',
                  'ファイルのダウンロードはブラウザの設定に従い保存されます'
                ].map((text, i) => (
                  <li key={i} className="text-sm" style={{ padding: '6px 0', paddingLeft: '20px', position: 'relative' as const }}>
                    <span style={{ position: 'absolute' as const, left: 0, fontWeight: 'bold' }}>&bull;</span>
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="hero-card__footer">
            <div className="text-muted text-xs mb-2">
              Powered by
            </div>
            <div className="text-sm font-semibold" style={{ color: 'var(--color-cyan-500)' }}>
              <MkdfLogo height={14} lightBg />
              <span style={{ margin: '0 4px', opacity: 0.3 }}>|</span>
              <DFFilesLogo size={13} variant="dark" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Auth / OTP-sent state - show authentication screen
  return (
    <div className="hero-gradient">
      <div className="hero-card">
        {/* Header */}
        <div className="hero-card__header">
          <div className="hero-card__logo">
            <MkdfLogo height={28} />
              <span className="header__brand-divider" style={{ margin: '0 8px', opacity: 0.3 }}>|</span>
              <DFFilesLogo size={24} variant="light" />
          </div>
          <div className="hero-card__tagline">
            セキュアなファイル共有ソリューション
          </div>
        </div>

        {/* Content */}
        <div className="hero-card__body">
          <h2 className="font-bold text-center mb-2" style={{ fontSize: 'var(--text-xl)' }}>
            メールアドレス認証
          </h2>

          <p className="text-muted text-sm text-center mb-6" style={{ lineHeight: 1.6 }}>
            ファイルにアクセスするには、メールアドレスの認証が必要です。
          </p>

          {/* Share Title */}
          <div className="card card__body mb-6" style={{ borderLeft: '4px solid var(--color-cyan-500)' }}>
            <div className="text-muted text-xs mb-1">
              共有タイトル
            </div>
            <div className="font-semibold">
              {shareTitle}
            </div>
          </div>

          {/* Masked Email */}
          <div className="info-box info-box--info text-center mb-6">
            <div className="text-xs mb-1">
              認証先メールアドレス
            </div>
            <div className="font-semibold" style={{ fontSize: 'var(--text-md)' }}>
              {maskedEmail}
            </div>
          </div>

          {/* Send OTP Button */}
          <button
            className="btn btn--primary btn--block mb-4"
            onClick={handleSendOTP}
            disabled={otpSending || retryAfter > 0}
          >
            {otpSending
              ? '送信中...'
              : retryAfter > 0
              ? `再送信可能まで ${retryAfter}秒`
              : phase === 'otp-sent'
              ? '認証コードを再送信'
              : '認証コードを送信'}
          </button>

          {/* Success Message */}
          {otpSuccess && (
            <div className="alert alert--success text-center">
              {otpSuccess}
            </div>
          )}

          {/* Error Message */}
          {otpError && (
            <div className="alert alert--error">
              <div>{otpError.message}</div>
              {otpError.remainingAttempts !== undefined && otpError.remainingAttempts > 0 && (
                <div className="text-xs mt-1">
                  残り試行回数: {otpError.remainingAttempts}回
                </div>
              )}
              {otpError.requiresResend && (
                <button
                  className="btn btn--primary btn--sm mt-2"
                  onClick={handleSendOTP}
                  disabled={otpSending || retryAfter > 0}
                >
                  {retryAfter > 0 ? `再送信可能まで ${retryAfter}秒` : '再送信'}
                </button>
              )}
            </div>
          )}

          {/* OTP Code Input */}
          <div className="form-group">
            <label className="form-label">
              認証コード
            </label>
            <input
              type="text"
              inputMode="numeric"
              placeholder="6桁の認証コード"
              value={otpCode}
              onChange={(e) => handleOtpCodeChange(e.target.value)}
              disabled={phase !== 'otp-sent' || (otpError?.requiresResend ?? false)}
              maxLength={6}
              className={`form-input ${otpError && !otpError.requiresResend ? 'form-input--error' : ''}`}
              style={{
                fontSize: '24px',
                fontWeight: 600,
                textAlign: 'center',
                letterSpacing: '8px'
              }}
            />
          </div>

          {/* Verify Button */}
          <button
            className="btn btn--success btn--block"
            onClick={handleVerifyOTP}
            disabled={otpCode.length !== 6 || otpVerifying || (otpError?.requiresResend ?? false)}
          >
            {otpVerifying ? '認証中...' : '認証する'}
          </button>
        </div>

        {/* Footer */}
        <div className="hero-card__footer">
          <div className="text-muted text-xs mb-2">
            Powered by
          </div>
          <div className="text-sm font-semibold" style={{ color: 'var(--color-cyan-500)' }}>
            <MkdfLogo height={14} lightBg />
              <span style={{ margin: '0 4px', opacity: 0.3 }}>|</span>
              <DFFilesLogo size={13} variant="dark" />
          </div>
        </div>
      </div>

      <MessageDialog {...dialogState} onClose={closeDialog} />
    </div>
  );
};

export default OTPVerification;
